/**
 * normalizePolicyForComparison - Normalizes policy data for display in Policy Comparison UI
 *
 * This is a simplified version that only uses policyData fields (no sourceQuote).
 *
 * Rules:
 * - Split inflation: COB rate grows monthly benefits; Face rate grows pool/totals
 * - If face rate is 0/undefined, pools/totals fall back to COB rate (with COB cap years)
 * - Single inflation: same rate for both monthly and pool
 * - Years used for CALC = min(yearsUntilClaim, capYears)
 * - Years shown in LABEL = capYears (when known)
 * - Step plans: apply full step increments only
 */

import {
  fundingPolicyTypeDefs,
  FundingPolicyType,
  formatCurrency,
  unlimitedMaxBenefitMagicNumber,
  carePhaseDefs,
  checkPolicyCalculationTypeEquals,
} from '@shared';
import { parseBioPercentToInflationRate } from '../util/policyQuoteCalculations';

interface NormalizedPolicy {
  fundingSourceId: string;
  fundingPolicyType?: FundingPolicyType;
  carrierId?: number; // InsuranceCarrier enum value
  carrier?: string; // Carrier name string (backwards compatible)
  productName?: string;
  initialMonthlyBenefit?: string;
  inflatedMonthlyBenefit?: string;
  initialTotalBenefit?: string;
  inflatedTotalBenefit?: string;
  inflationDescription?: string;
  benefitPeriodMonths?: number;
  eliminationPeriodLabel?: string;
  productDescription?: string;
  paymentMethod?: 'Indemnity' | 'Reimbursement';
  premiumMonthlyCost?: number;
  lumpSumPayment?: number;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatBenefit(value?: number) {
  if (value === undefined || value === null) return '-';
  return value >= unlimitedMaxBenefitMagicNumber ? 'Unlimited' : formatCurrency(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatInflatedMonthlyBenefit(
  inflatedMonthlyBenefit: number | undefined,
  inflatedMonthlyBenefitBase: number | undefined,
  inflatedMonthlyBenefitRider: number | undefined,
): string | undefined {
  // If we have both base and rider, and they differ, show them side by side
  if (
    isFiniteNumber(inflatedMonthlyBenefitBase) &&
    isFiniteNumber(inflatedMonthlyBenefitRider) &&
    inflatedMonthlyBenefitBase !== inflatedMonthlyBenefitRider
  ) {
    const baseFormatted = formatBenefit(inflatedMonthlyBenefitBase);
    const riderFormatted = formatBenefit(inflatedMonthlyBenefitRider);
    return `${baseFormatted} Base / ${riderFormatted} Rider`;
  }

  // Otherwise, use the standard inflated monthly benefit
  return formatBenefit(inflatedMonthlyBenefit);
}

/** 0.02, 2, "2", "2%" → 0.02 */
function toNormalizedRate(inputRate: unknown): number | undefined {
  if (inputRate === null || inputRate === undefined) return undefined;

  if (typeof inputRate === 'string') {
    const trimmed = inputRate.trim();

    if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed === 'No BIO') return undefined;

    if (trimmed.includes('%')) {
      const viaBioRate = parseBioPercentToInflationRate(trimmed);

      if (Number.isFinite(viaBioRate)) return viaBioRate as number;
    }

    const numericValue = parseFloat(trimmed.replace('%', ''));

    if (!Number.isFinite(numericValue)) return undefined;

    return numericValue >= 1 ? numericValue / 100 : numericValue;
  }

  if (typeof inputRate === 'number' && Number.isFinite(inputRate)) {
    return inputRate >= 1 ? inputRate / 100 : inputRate;
  }

  return undefined;
}

function formatPercentLabel(rateOrRaw?: number | unknown): string {
  const normalizedRate = toNormalizedRate(rateOrRaw);

  if (!isFiniteNumber(normalizedRate)) return 'N/A';

  const percent = normalizedRate * 100;

  return percent % 1 === 0 ? `${percent.toFixed(0)}%` : `${percent.toFixed(1)}%`;
}

const formatYearsLabel = (inputYears: number) => {
  const roundedToTenth = Math.floor(inputYears * 10) / 10;

  return roundedToTenth % 1 === 0 ? String(Math.floor(roundedToTenth)) : String(roundedToTenth);
};

function buildEliminationPeriodLabel(
  homeCareWaitingDays?: number,
  facilityCareWaitingDays?: number,
  fundingPolicyType?: FundingPolicyType,
): string | undefined {
  if (!isFiniteNumber(homeCareWaitingDays) && !isFiniteNumber(facilityCareWaitingDays)) {
    return undefined;
  }

  const homeDays = isFiniteNumber(homeCareWaitingDays) ? homeCareWaitingDays : 0;
  const facilityDays = isFiniteNumber(facilityCareWaitingDays) ? facilityCareWaitingDays : 0;

  let label =
    homeDays === facilityDays
      ? `${homeDays} days`
      : `${homeDays} days home care / ${facilityDays} days facility care`;

  // CareMatters special handling
  const isCareMattersPolicy =
    fundingPolicyType === FundingPolicyType.careMattersTwo ||
    fundingPolicyType === FundingPolicyType.jointCareMatters;

  if (isCareMattersPolicy) {
    label += ' (paid retroactively in month 4)';
  }

  return label;
}

function buildGrowthLabel(
  fundingPolicySource: PolicyFundingSource,
  phaseCalculations: SinglePhaseCalculations[],
): string | undefined {
  const {
    policyInflationProtection,
    policyInflationProtectionOnFaceAmount,
    policyInflationProtectionOnCOBAmount,
    isSimpleInflationProtection,
    policyInflationProtectionYears,
    policyType,
  } = fundingPolicySource;

  const policyInflationProtectionRate = toNormalizedRate(policyInflationProtection);
  const inflationPeriodYears =
    phaseCalculations[0]?.inflationPeriodYears ?? policyInflationProtectionYears ?? 0;
  const inflationYearsLabel = inflationPeriodYears ? formatYearsLabel(inflationPeriodYears) : '';
  const simpleOrCompoundLabel = isSimpleInflationProtection ? 'Simple' : 'Compound';
  const inflationMethodAndDurationLabel = `${simpleOrCompoundLabel}${inflationYearsLabel ? ` (${inflationYearsLabel} yrs)` : ''}`;

  if (checkPolicyCalculationTypeEquals(policyType, [FundingPolicyType.annuityHybrid])) {
    return `Variable Growth ${inflationYearsLabel ? ` (${inflationYearsLabel} yrs)` : ''}`;
  }

  if (isFiniteNumber(policyInflationProtectionRate)) {
    return `${formatPercentLabel(policyInflationProtectionRate)} ${inflationMethodAndDurationLabel}`;
  }

  // other wise return inflation for face amount and or cob amount along with simple or compound label
  const faceInflationRateLabel = policyInflationProtectionOnFaceAmount
    ? formatPercentLabel(policyInflationProtectionOnFaceAmount)
    : undefined;
  const cobInflationRateLabel = policyInflationProtectionOnCOBAmount
    ? formatPercentLabel(policyInflationProtectionOnCOBAmount)
    : undefined;
  const labelPieces = [];
  if (faceInflationRateLabel) labelPieces.push(`${faceInflationRateLabel} Face`);
  if (cobInflationRateLabel) labelPieces.push(`${cobInflationRateLabel} COB`);
  return `${labelPieces.join(', ')}${labelPieces.length ? ' ' : ''}${inflationMethodAndDurationLabel}`;
}

function calculateTotalBenefits(
  policyFundingSource: PolicyFundingSource,
  phaseCalculations: SinglePhaseCalculations[],
  firstCarePhaseIndex: number,
): {
  initialTotalBenefit?: number;
  inflatedTotalBenefit?: number;
} {
  // Get policy's total LTC benefit pool
  const { policyBenefitIsUnlimited, policyMaximumBenefitAmount, policyContinuationBenefitAmount } =
    policyFundingSource;
  const initialTotalBenefit = policyBenefitIsUnlimited
    ? unlimitedMaxBenefitMagicNumber
    : (policyMaximumBenefitAmount ?? 0) + (policyContinuationBenefitAmount ?? 0);
  const firstCarePhaseCalc = Array.isArray(phaseCalculations)
    ? phaseCalculations[firstCarePhaseIndex]
    : undefined;
  const inflatedTotalBenefit = firstCarePhaseCalc?.inflatedMaxBenefitAmount ?? 0;

  if (
    checkPolicyCalculationTypeEquals(policyFundingSource.policyType, [
      FundingPolicyType.annuityHybrid,
    ]) ||
    policyFundingSource.policyType === FundingPolicyType.indexedAnnuityCare
  ) {
    // Currently we do not extract or collect the initial total benefit for annuity hybrid policies
    // we only collect the projected inflated total benefit
    return {
      initialTotalBenefit: undefined,
      inflatedTotalBenefit,
    };
  }
  return {
    initialTotalBenefit,
    inflatedTotalBenefit,
  };
}

function calculateMonthlyBenefit(
  phaseCalcsForPolicy: SinglePhaseCalculations[] | undefined,
  daysPerMonthForCalc: number,
  policyType: FundingPolicyType,
  firstCarePhaseIndex?: number,
): {
  initialMonthlyBenefit?: number;
  inflatedMonthlyBenefit?: number;
  inflatedMonthlyBenefitBase?: number;
  inflatedMonthlyBenefitRider?: number;
  initialDailyBenefit?: number;
  inflatedDailyBenefit?: number;
} {
  let initialMonthlyBenefit: number | undefined;
  let inflatedMonthlyBenefit: number | undefined;
  let inflatedDailyBenefitBaseAmount: number | null;
  let inflatedDailyBenefitRiderAmount: number | null;
  let inflatedMonthlyBenefitBase: number | undefined;
  let inflatedMonthlyBenefitRider: number | undefined;
  let initialDailyBenefit: number | undefined;
  let inflatedDailyBenefit: number | undefined;

  // Try to get from policyPhaseCalculations for the first care phase
  const firstCarePhaseCalc =
    Array.isArray(phaseCalcsForPolicy) && isFiniteNumber(firstCarePhaseIndex)
      ? phaseCalcsForPolicy[firstCarePhaseIndex!]
      : undefined;

  if (firstCarePhaseCalc) {
    if (
      checkPolicyCalculationTypeEquals(policyType, [
        FundingPolicyType.hybridLifeIra,
        FundingPolicyType.hybridLifeInsurance,
      ])
    ) {
      // Initial daily benefit (base amount)
      initialDailyBenefit = firstCarePhaseCalc.dailyBenefitAmount;

      // For hybrid policies, track base and rider separately
      inflatedDailyBenefitRiderAmount = firstCarePhaseCalc.inflatedDailyBenefitAmountRider;
      inflatedDailyBenefitBaseAmount = checkPolicyCalculationTypeEquals(policyType, [
        FundingPolicyType.hybridLifeIra,
      ])
        ? firstCarePhaseCalc.dailyBenefitAmountBase
        : firstCarePhaseCalc.inflatedDailyBenefitAmountBase;

      // Calculate monthly benefits for base and rider
      if (isFiniteNumber(inflatedDailyBenefitBaseAmount)) {
        inflatedMonthlyBenefitBase = inflatedDailyBenefitBaseAmount! * daysPerMonthForCalc;
      }
      if (isFiniteNumber(inflatedDailyBenefitRiderAmount)) {
        inflatedMonthlyBenefitRider = inflatedDailyBenefitRiderAmount! * daysPerMonthForCalc;
      }

      // Total inflated daily benefit is the max of base, rider, or general amount
      const finalInflatedDailyBenefitAmount = Math.max(
        inflatedDailyBenefitBaseAmount ?? 0,
        inflatedDailyBenefitRiderAmount ?? 0,
        firstCarePhaseCalc.inflatedDailyBenefitAmount ?? 0,
      );
      inflatedDailyBenefit = finalInflatedDailyBenefitAmount;
    } else if (checkPolicyCalculationTypeEquals(policyType, [FundingPolicyType.annuityHybrid])) {
      initialDailyBenefit = undefined;
      inflatedDailyBenefit = firstCarePhaseCalc?.inflatedDailyBenefitAmount;
    } else {
      // Standard policies
      initialDailyBenefit = firstCarePhaseCalc.dailyBenefitAmount;
      inflatedDailyBenefit = firstCarePhaseCalc?.inflatedDailyBenefitAmount ?? initialDailyBenefit;
    }

    if (isFiniteNumber(initialDailyBenefit)) {
      initialMonthlyBenefit = initialDailyBenefit! * daysPerMonthForCalc;
    }

    if (isFiniteNumber(inflatedDailyBenefit)) {
      inflatedMonthlyBenefit = inflatedDailyBenefit! * daysPerMonthForCalc;
    }
  }

  return {
    initialMonthlyBenefit,
    inflatedMonthlyBenefit,
    inflatedMonthlyBenefitBase,
    inflatedMonthlyBenefitRider,
    initialDailyBenefit,
    inflatedDailyBenefit,
  };
}

export function normalizePoliciesForComparison(
  policyFundingSources: MultipleFundingSources['ltcPolicy'],
  client: Client,
): NormalizedPolicy[] {
  const daysPerMonthForCalc = 30;

  return Object.entries(policyFundingSources).map(([fundingSourceId, policyFundingSource]) => {
    const fundingPolicyType = policyFundingSource.policyType as FundingPolicyType;

    // Phase 2: Basic Policy Info
    const policyTypeDef = fundingPolicyTypeDefs[fundingPolicyType];
    const productName = policyTypeDef?.label;
    const productDescription = policyTypeDef?.description;
    const carrierId = policyTypeDef?.carrierId;
    const carrier = policyTypeDef?.carrier;
    const premiumMonthlyCost = policyFundingSource.policyPremiumMonthlyCost ?? undefined;
    const lumpSumPayment = policyFundingSource.policyLumpSumPayment ?? undefined;

    // Benefit Period & Elimination Period
    const benefitPeriodMonths = policyFundingSource.policyBenefitPeriodMonths ?? undefined;
    const eliminationPeriodLabel = buildEliminationPeriodLabel(
      policyFundingSource.homeCareWaitingPeriodDays ?? undefined,
      policyFundingSource.facilityCareWaitingPeriodDays ?? undefined,
      fundingPolicyType,
    );

    // Monthly Benefit Calculation
    const phaseCalcsForPolicy = client.policyPhaseCalculations?.[fundingSourceId];

    // Determine the first care phase index (early care = 0, or current care phase)
    const currentCarePhase = client.intakeSurvey?.clientCurrentCarePhase;
    const firstCarePhaseIndex = currentCarePhase ? carePhaseDefs[currentCarePhase].index : 0;

    const {
      initialMonthlyBenefit,
      inflatedMonthlyBenefit,
      inflatedMonthlyBenefitBase,
      inflatedMonthlyBenefitRider,
    } = calculateMonthlyBenefit(
      phaseCalcsForPolicy,
      daysPerMonthForCalc,
      fundingPolicyType,
      firstCarePhaseIndex,
    );

    // Payment Method
    const paymentMethod: 'Indemnity' | 'Reimbursement' | undefined =
      policyFundingSource.isIndemnityPolicyPayment ? 'Indemnity' : 'Reimbursement';

    // Total benefit calculations
    const { initialTotalBenefit, inflatedTotalBenefit } = calculateTotalBenefits(
      policyFundingSource,
      phaseCalcsForPolicy,
      firstCarePhaseIndex,
    );

    // Growth label generation
    const growthLabel = buildGrowthLabel(policyFundingSource, phaseCalcsForPolicy);

    return {
      fundingSourceId,
      fundingPolicyType,
      carrierId,
      carrier,
      productDescription,
      productName,
      inflationDescription: growthLabel,
      benefitPeriodMonths,
      eliminationPeriodLabel,
      paymentMethod,
      lumpSumPayment,
      premiumMonthlyCost,
      initialMonthlyBenefit: initialMonthlyBenefit ? formatBenefit(initialMonthlyBenefit) : '-',
      inflatedMonthlyBenefit:
        inflatedMonthlyBenefit || inflatedMonthlyBenefitBase || inflatedMonthlyBenefitRider
          ? formatInflatedMonthlyBenefit(
              inflatedMonthlyBenefit,
              inflatedMonthlyBenefitBase,
              inflatedMonthlyBenefitRider,
            )
          : '-',
      initialTotalBenefit: formatBenefit(initialTotalBenefit),
      inflatedTotalBenefit: formatBenefit(inflatedTotalBenefit),
    };
  });
}
